import {styled} from '@mui/material/styles';
import Typography, {TypographyProps} from '@mui/material/Typography';
import {CSSProperties, ReactNode} from 'react';

import {shouldNotForwardProp} from 'utils/styled';

const SPlaceholder = styled(
  'div',
  shouldNotForwardProp('minHeight', 'padding')
)<{minHeight?: CSSProperties['minHeight']; padding?: number}>(({theme, minHeight, padding}) => ({
  height: '100%',
  minHeight,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(padding ?? 4),
}));

const SPlaceholderContent = styled('div')(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  alignItems: 'center',
  textAlign: 'center',
}));

const STextWrapper = styled(
  'div',
  shouldNotForwardProp('size')
)<{size: 'small' | 'medium'}>(({theme, size}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: size === 'small' ? theme.spacing(2) : theme.spacing(4),
}));

const SIconWrapper = styled(
  'div',
  shouldNotForwardProp('size', 'padding')
)<{size: 'small' | 'medium'; padding: CSSProperties['padding']}>(({theme, size, padding}) => ({
  width: 'max-content',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',

  padding: padding ? padding : size === 'small' ? theme.spacing(9.5) : theme.spacing(11),
  borderRadius: '50%',
  backgroundColor: theme.palette.background.grey,

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6),

    '& svg': {
      width: 32,
      height: 32,
    },
  },
}));

export type PlaceholderProps = {
  icon?: ReactNode;
  heading: string;
  subheading?: ReactNode;
  actions?: ReactNode;
  minHeight?: CSSProperties['minHeight'];
  headingVariant?: TypographyProps['variant'];
  subHeadingVariant?: TypographyProps['variant'];
  subheadingColor?: TypographyProps['color'];
  size?: 'small' | 'medium';
  padding?: number;
  iconPadding?: CSSProperties['padding'];
};

export function Placeholder({
  icon,
  heading,
  subheading,
  actions,
  minHeight,
  headingVariant = 'h5',
  subHeadingVariant = 'body2',
  subheadingColor,
  size = 'medium',
  padding,
  iconPadding,
}: PlaceholderProps) {
  return (
    <SPlaceholder minHeight={minHeight} padding={padding}>
      <SPlaceholderContent>
        {icon && (
          <SIconWrapper size={size} padding={iconPadding}>
            {icon}
          </SIconWrapper>
        )}

        <STextWrapper size={size}>
          <Typography variant={headingVariant}>{heading}</Typography>

          {subheading && (
            <Typography variant={subHeadingVariant} color={subheadingColor}>
              {subheading}
            </Typography>
          )}
        </STextWrapper>

        {actions}
      </SPlaceholderContent>
    </SPlaceholder>
  );
}
